import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SupportCard from "../components/SupportCard";
import { HelpCircle, ShieldCheck, Zap, MessageCircle } from "lucide-react";

const FAQ = [
  {
    q: "What does FlowFinder actually do?",
    a: "FlowFinder is a momentum scanner. We pull live OHLCV data and compute RSI, MACD, VWAP, Bollinger Bands, RVOL and EMA 9/20/50 on stocks, ETFs, crypto-proxy ETFs and futures, then rank each setup from 1 to 100 so you instantly see what's worth watching.",
  },
  {
    q: "Is the market data live?",
    a: "Quotes and 1-minute OHLCV are streamed live from Finnhub via WebSocket during US market hours. Indicators (RSI, MACD, VWAP, EMA, RVOL, Bollinger) are recomputed continuously from the rolling tick buffer. Outside market hours we display the official last-traded price with a clear DELAYED badge. The data badge at the top of your terminal always tells you the exact source and how recent the data is.",
  },
  {
    q: "How do I save a custom scan?",
    a: "From the dashboard, configure your filters in the Filter Builder, then tap 'Save current as preset', give it a name and emoji, and it'll appear in 'My Scans' for one-click reload. You can favorite, rename or delete saved scans on hover.",
  },
  {
    q: "What's the difference between the plans?",
    a: "Free includes RSI & MACD filters on stocks & ETFs with daily live data. Pro ($19.99/mo) unlocks live OHLCV, VWAP / Bollinger / EMA filters, futures and 25 saved scans. Elite ($49.99/mo) adds premium scanner speed and priority support.",
  },
  {
    q: "Will FlowFinder give me buy/sell signals?",
    a: "No. FlowFinder surfaces clean technical setups for you to research. It is for educational purposes only and is not financial advice. Market data may be delayed depending on provider.",
  },
];

export default function Support() {
  return (
    <div className="min-h-screen flex flex-col" data-testid="support-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[1000px] w-full mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
        <div className="absolute inset-x-0 top-0 -z-10 h-96 pointer-events-none" style={{ background: "radial-gradient(800px 400px at 50% 0%, rgba(0,255,156,0.10), transparent 60%)" }} />
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md mb-5">
            <MessageCircle className="w-3.5 h-3.5 text-[var(--bull)]" />
            <span className="text-xs text-white/80 tracking-tight">Help &amp; Contact</span>
          </div>
          <h1 className="text-4xl sm:text-6xl tracking-[-0.02em] font-semibold text-white">
            We're <span className="aurora-text">here to help.</span>
          </h1>
          <p className="text-white/55 mt-4 max-w-xl mx-auto leading-relaxed">
            Real humans, real quick replies. Drop us a note and we'll get back to you within one business day.
          </p>
        </div>

        <SupportCard className="max-w-2xl mx-auto" />

        <div className="grid sm:grid-cols-3 gap-3 mt-8">
          <Stat icon={Zap} label="Avg first reply" value="~6h" accent="var(--bull)" />
          <Stat icon={ShieldCheck} label="Issues resolved" value="98%" accent="#6c5ce7" />
          <Stat icon={HelpCircle} label="FAQs answered" value="24/7" accent="#ffd166" />
        </div>

        <section className="mt-12">
          <div className="label-mono">FAQ</div>
          <h2 className="text-2xl sm:text-3xl text-white font-semibold tracking-tight mt-1">Common questions</h2>
          <div className="mt-5 space-y-2">
            {FAQ.map((f, i) => (
              <details key={i} className="card-surface px-5 py-4 group" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-white text-[15px] font-medium tracking-tight">{f.q}</span>
                  <span className="text-white/40 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-white/60 text-sm leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="card-surface p-5 mt-10 text-xs text-white/55 leading-relaxed">
          Educational purposes only. Not financial advice. Market data may be delayed depending on provider.
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" style={{ color: accent }} /><div className="label-mono">{label}</div></div>
      <div className="text-white text-2xl font-semibold tracking-tight mt-1">{value}</div>
    </div>
  );
}
